import React, { useEffect, useState, useRef } from "react";
import {
  Col,
  Row,
  Space,
  Spin,
  Typography,
  Button,
  Table,
  Tooltip,
  message,
  Tag,
  DatePicker,
  Tabs,
  Statistic,
  Empty,
  notification,
} from "antd";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Application, ApplicationAction } from "../../model_gen/application";
import { usePermissions } from "../../PermissionsProvider";
import { ApplicationService } from "../../services/ApplicationService";
import moment from "moment/moment";
import { Config } from "../../model_gen/config";
import ConfigurationPanel from "../configuration/ConfigurationPanel";
import {
  CopyOutlined,
  SettingOutlined,
  AppstoreOutlined,
  HistoryOutlined,
  FileTextOutlined,
  SearchOutlined,
  CalendarOutlined,
  PlusOutlined,
  ApiOutlined,
} from "@ant-design/icons";
import { RequestsService } from "../../services/RequestsService";
import { ColumnsType } from "antd/es/table";
import { ChatCompletionRequest } from "../../model_gen/chat";
import type { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import dayjs from "dayjs";
import { ReactComponent as AiStars } from "../../assets/svg/ai-creation.svg";
import { IntentService } from "../../services/IntentService";
import { IntentGroup } from "../../model_gen/intent";
import { RequestViewButton } from "../shared/button/Buttons";
import DatasetsTable from "../Finetune/Datasets/DatasetsTable";
import { DatasetService } from "../../services/DatasetService";
import { FinetuneDataset } from "../../model_gen/finetuning";
import ModelTable from "../shared/ModelTable";

dayjs.extend(utc);
const { TabPane } = Tabs;

const ApplicationActionOverview: React.FC = () => {
  const { apiKey } = usePermissions();
  const { applicationId, actionId } = useParams<{
    applicationId: string;
    actionId: string;
  }>();
  const [application, setApplication] = useState<Application | null>(null);
  const [config, setConfig] = useState<Config>();
  const [applicationAction, setApplicationAction] =
    useState<ApplicationAction | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [requests, setRequests] = useState<any[]>([]);
  const [requestsTotal, setRequestsTotal] = useState(0);
  const [requestsCurrentPage, setRequestsCurrentPage] = useState(1);
  const [requestPageSize, setRequestPageSize] = useState<number>(10);
  const [requestsLoading, setRequestsLoading] = useState(false);
  const requestsByPageRef = useRef<Record<number, any[]>>({});
  const [initialLoadDate] = useState<number>(Date.now());
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const [intentGroup, setIntentGroup] = useState<IntentGroup | null>(null);
  const [originalConfig, setOriginalConfig] = useState<Config>();
  const [activeTab, setActiveTab] = useState<string>("overview");
  const [datasets, setDatasets] = useState<FinetuneDataset[]>([]);
  const [datasetsLoading, setDatasetsLoading] = useState<boolean>(false);
  const [datasetsTotal, setDatasetsTotal] = useState<number>(0);
  const [datasetsCurrentPage, setDatasetsCurrentPage] = useState<number>(1);
  const datasetsPageSize = 10;
  const navigate = useNavigate();

  const styles = {
    hoverRow: {
      cursor: "pointer",
      transition: "background-color 0.2s ease",
    },
    monospaceFont: {
      fontFamily: "monospace",
      fontSize: "13px",
    },
    statsCard: (color: string) => ({
      background: `linear-gradient(135deg, ${color}10 0%, #f8f8f8 100%)`,
      padding: "14px 16px",
      borderRadius: "12px",
      height: "100%",
      border: `1px solid ${color}30`,
      boxShadow: "0 2px 6px rgba(0, 0, 0, 0.03)",
      transition: "all 0.3s ease",
      display: "flex",
      alignItems: "center",
    }),
    tableContainer: {
      borderRadius: "12px",
      overflow: "hidden",
      boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
    },
    contentCard: {
      background: "#fff",
      padding: "24px",
      borderRadius: "12px",
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
      border: "1px solid #f0f0f0",
    },
    detailsContainer: {
      borderRadius: "12px",
      border: "1px solid #f0f0f0",
      overflow: "hidden",
    },
    detailsHeader: {
      borderBottom: "1px solid #f0f0f0",
      padding: "16px 24px",
      background: "#fafafa",
    },
    detailsContent: {
      padding: "24px",
    },
    tabIcon: {
      marginRight: "8px",
    },
    subtitle: {
      fontSize: "14px",
      color: "#8c8c8c",
      display: "block",
      marginBottom: "8px",
    },
    aiIconStyle: {
      width: "12px",
      height: "12px",
      color: "#8c8c8c",
      marginLeft: "4px",
      verticalAlign: "text-top",
    },
    tagStyle: {
      padding: "4px 10px",
      borderRadius: "4px",
      fontSize: "12px",
    },
    actionButton: (hovered: boolean) => ({
      borderRadius: "6px",
      color: "#1890ff",
      display: "flex",
      alignItems: "center",
      padding: "0 10px",
      height: "32px",
      transition: "all 0.3s",
      background: hovered ? "#f0f7ff" : "transparent",
      boxShadow: hovered ? "0 2px 4px rgba(24, 144, 255, 0.1)" : "none",
    }),
  };

  const getConfig = () => {
    ApplicationService.getApplicationActionConfig(
      Number(applicationId),
      Number(actionId),
      null,
      apiKey
    ).subscribe(
      (config: Config) => {
        setConfig(config);
        setOriginalConfig(config);
      },
      (error: any) => {
        console.error(error);
      }
    );
  };

  const updateConfig = (
    config: Partial<Config>,
    loadingHandler: (loading: boolean) => void | null,
    save: boolean = true
  ) => {
    setConfig((prevConfig: Config | undefined) => {
      if (!prevConfig) {
        return config;
      }
      return {
        ...prevConfig,
        ...config,
      };
    });
    if (!save) {
      return;
    }
    ApplicationService.setApplicationActionConfig(
      Number(applicationId),
      Number(actionId),
      config,
      loadingHandler,
      apiKey
    ).subscribe(
      (config: Config) => {
        setConfig(config);
      },
      (error: any) => {
        console.error(error);
        message.error("Failed to update configuration");
      }
    );
  };

  const resetConfig = () => {
    setConfig(undefined);
    ApplicationService.resetApplicationActionConfig(
      Number(applicationId),
      Number(actionId),
      null,
      apiKey
    ).subscribe(
      () => {
        getConfig();
        message.success("Configuration reset successfully");
      },
      (error: any) => {
        console.error(error);
        message.error("Failed to reset configuration");
      }
    );
  };

  const fetchRequests = (page: number) => {
    if (requestsByPageRef.current[page] && !startDate && !endDate) {
      setRequests(requestsByPageRef.current[page]);
      return;
    }

    const offset = (page - 1) * requestPageSize;
    setRequestsLoading(true);

    const start = startDate
      ? dayjs(startDate).startOf("day").utc().valueOf()
      : -1;
    const endTemp = endDate
      ? dayjs(endDate).add(1, "day").startOf("day").utc().valueOf()
      : initialLoadDate;
    const end = endTemp > initialLoadDate ? initialLoadDate : endTemp;

    RequestsService.getApplicationActionRequests(
      Number(applicationId),
      Number(actionId),
      start,
      end,
      offset,
      requestPageSize,
      setRequestsLoading,
      apiKey
    ).subscribe(
      (data: any) => {
        setRequests(data.items || []);
        setRequestsTotal(data.total || 0);
        requestsByPageRef.current[page] = data.items || [];
        setRequestsLoading(false);
      },
      (error: any) => {
        console.error(error);
        setRequestsLoading(false);
        message.error("Failed to fetch requests");
      }
    );
  };

  const handleClearFilters = () => {
    setStartDate(null);
    setEndDate(null);
  };

  const fetchDatasets = (page: number) => {
    if (!applicationId || !actionId) return;

    const offset = (page - 1) * datasetsPageSize;

    DatasetService.getDatasetsByIntent(
      Number(applicationId),
      Number(actionId),
      offset,
      datasetsPageSize,
      setDatasetsLoading,
      apiKey
    ).subscribe(
      (response: any) => {
        setDatasets(response.items || []);
        setDatasetsTotal(response.total || 0);
      },
      (error: any) => {
        console.error("Error fetching datasets:", error);
        notification.error({
          message: "Error",
          description: `Failed to fetch datasets: ${error}`,
        });
      }
    );
  };

  useEffect(() => {
    if (applicationId) {
      ApplicationService.getApplication(
        Number(applicationId),
        setIsLoading,
        apiKey
      ).subscribe(
        (app: Application) => {
          setApplication(app);
          ApplicationService.getApplicationAction(
            Number(applicationId),
            Number(actionId),
            null,
            apiKey
          ).subscribe((action: ApplicationAction) => {
            setApplicationAction(action);
            getConfig();
          });
        },
        (error: any) => {
          console.error(error);
          message.error("Failed to load application");
        }
      );
      if (applicationId && actionId) {
        IntentService.getIntentGroupByApplicationAndIntent(
          Number(applicationId),
          Number(actionId),
          () => {},
          apiKey
        ).subscribe(
          (intentGroup: IntentGroup) => {
            setIntentGroup(intentGroup);
          },
          (error: any) => {
            console.error(error);
          }
        );
      }
    }
  }, [applicationId, actionId]);

  useEffect(() => {
    if (applicationId && actionId && activeTab === "requests") {
      if (!requestsByPageRef.current[requestsCurrentPage]) {
        fetchRequests(requestsCurrentPage);
      } else {
        setRequests(requestsByPageRef.current[requestsCurrentPage]);
      }
    }
  }, [
    applicationId,
    actionId,
    requestsCurrentPage,
    activeTab,
    requestPageSize,
  ]);

  useEffect(() => {
    if (
      (!startDate || startDate == null) &&
      (!endDate || endDate == null) &&
      activeTab === "requests"
    ) {
      requestsByPageRef.current = {};
      setRequestsCurrentPage(1);
      fetchRequests(1);
    }
  }, [startDate, endDate, activeTab]);

  useEffect(() => {
    if (applicationId && actionId && activeTab === "datasets") {
      fetchDatasets(datasetsCurrentPage);
    }
  }, [
    applicationId,
    actionId,
    datasetsCurrentPage,
    activeTab,
    datasetsPageSize,
  ]);

  // Add global CSS for interactive rows
  useEffect(() => {
    // Add styles for interactive rows
    const style = document.createElement("style");
    style.innerHTML = `
      .interactive-row:hover td {
        background-color: #f5f8ff !important;
      }
      .interactive-row {
        cursor: pointer;
        transition: all 0.2s ease;
      }
      .details-button:hover {
        background-color: #f0f7ff !important;
        box-shadow: 0 2px 4px rgba(24, 144, 255, 0.1) !important;
      }
      .custom-tabs .ant-tabs-tab {
        transition: all 0.3s ease;
        border-radius: 6px 6px 0 0 !important;
        padding: 8px 16px !important;
      }
      .custom-tabs .ant-tabs-tab:hover {
        color: #1890ff;
      }
      .custom-tabs .ant-tabs-tab-active {
        background-color: #f0f7ff !important;
      }
      .ant-table-tbody > tr.ant-table-row:hover > td {
        background-color: #f5f8ff !important;
      }
      .ant-statistic-content-value {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        max-width: 100%;
      }
      .ant-statistic-title {
        margin-bottom: 4px !important;
      }
      .ant-tag {
        border-radius: 4px;
        padding: 3px 8px;
      }
      .intent-overview-container .ant-btn:hover {
        transform: translateY(-1px);
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.09);
      }
      .intent-overview-container .ant-btn {
        transition: all 0.2s ease;
      }
      /* Fix statistic icon alignment */
      .ant-statistic-content {
        display: flex;
        align-items: center;
      }
      .ant-statistic-content-prefix {
        display: flex;
        align-items: center;
      }
      .ant-statistic-content-value {
        display: inline-flex;
        align-items: center;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const requestColumns: ColumnsType<ChatCompletionRequest> = [
    {
      title: "Date Created",
      dataIndex: "date_created",
      key: "date_created",
      width: "15%",
      render: (timestamp: number) => {
        const date = moment(timestamp);
        return (
          <Tooltip title={date.format("MMMM D, YYYY h:mm:ss A")}>
            <div>
              <div style={{ fontWeight: 500 }}>
                {date.format("MMM D, YYYY")}
              </div>
              <div style={{ color: "#8c8c8c", fontSize: "12px" }}>
                {date.format("h:mm A")}
              </div>
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: "Model",
      key: "model",
      render: (_, request: ChatCompletionRequest) => {
        return <Typography.Text>{request.params?.model}</Typography.Text>;
      },
    },
    {
      title: (
        <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
          Tags
          <AiStars style={styles.aiIconStyle} />
        </div>
      ),
      key: "tags",
      width: "50%",
      render: (_, request: ChatCompletionRequest) => {
        try {
          const metaData = JSON.parse(
            (request.metadata?._maitai_request_meta as string) || "{}"
          );
          const tags = metaData.tags || [];
          return (
            <Space wrap size={4}>
              {tags.map((tag: string) => (
                <Tag key={tag} color="blue" style={styles.tagStyle}>
                  {tag}
                </Tag>
              ))}
              {tags.length === 0 && (
                <Typography.Text type="secondary">—</Typography.Text>
              )}
            </Space>
          );
        } catch (e) {
          return <Typography.Text type="secondary">—</Typography.Text>;
        }
      },
    },
    {
      title: "Request ID",
      key: "request_id",
      width: "20%",
      render: (_, request: ChatCompletionRequest) => {
        const requestId = request.request_id || "";
        const shortId =
          requestId.length > 12
            ? `${requestId.substring(0, 6)}...${requestId.substring(
                requestId.length - 6
              )}`
            : requestId;

        return (
          <Space>
            <Typography.Text style={styles.monospaceFont} copyable={false}>
              {shortId}
            </Typography.Text>
            <Button
              icon={<CopyOutlined />}
              size="small"
              type="text"
              onClick={(e) => {
                e.stopPropagation();
                navigator.clipboard.writeText(requestId);
                message.success({
                  content: "Request ID copied to clipboard",
                  icon: <CopyOutlined style={{ color: "#52c41a" }} />,
                });
              }}
            />
          </Space>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: "15%",
      align: "center",
      render: (_, record: ChatCompletionRequest) => (
        <RequestViewButton
          apiKey={apiKey}
          request={record}
          to={`/application/${
            record.application_id
          }/session/${encodeURIComponent(record.session_id)}/request/${
            record.request_id
          }`}>
          Details
        </RequestViewButton>
      ),
    },
  ];

  if (isLoading) {
    return (
      <div
        className="loading-container"
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "calc(100vh - 200px)",
        }}>
        <Spin size="large" />
      </div>
    );
  }

  const modalTitle = (
    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
      <SettingOutlined />
      <span>Configuration Settings</span>
    </div>
  );

  // Parse tags from metadata
  const getTags = () => {
    try {
      return JSON.parse(
        (applicationAction?.meta?.metadata?.tags as string) || "[]"
      );
    } catch (e) {
      return [];
    }
  };

  // Parse description from metadata
  const getDescription = () => {
    try {
      return (applicationAction?.meta?.metadata?.intent as string) || "-";
    } catch (e) {
      return "-";
    }
  };

  return (
    <div
      className="intent-overview-container"
      style={{ padding: "24px", maxWidth: "1200px", margin: "0 auto" }}>
      {/* Header */}
      <div className="page-header" style={{ marginBottom: "32px" }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Space direction="vertical" size={4}>
              <Typography.Text type="secondary">
                <Link to="/applications">Applications</Link> /{" "}
                <Link to={`/application/${applicationId}`}>
                  {application?.application_name}
                </Link>{" "}
                / {applicationAction?.action_type}
              </Typography.Text>
              <Typography.Title
                level={2}
                style={{ margin: 0, fontWeight: 600 }}>
                {applicationAction?.action_type}
              </Typography.Title>
              <Typography.Text type="secondary" style={{ fontSize: "14px" }}>
                Monitor request traffic, configure settings, and view
                performance metrics
              </Typography.Text>
            </Space>
          </Col>
          <Col>
            <Space size={12}></Space>
          </Col>
        </Row>
      </div>

      {/* Main Content */}
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        type="card"
        className="custom-tabs"
        tabBarStyle={{ marginBottom: "24px" }}
        tabBarGutter={12}
        animated={{ inkBar: true, tabPane: true }}>
        {/* Overview Tab */}
        <TabPane
          tab={
            <span
              style={{
                padding: "0 4px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}>
              <AppstoreOutlined style={styles.tabIcon} />
              Overview
            </span>
          }
          key="overview">
          <div style={styles.contentCard}>
            <Row gutter={[24, 24]}>
              {/* Stats Row */}
              <Col span={24}>
                <Row gutter={[16, 16]}>
                  <Col xs={24} sm={8}>
                    <div style={styles.statsCard("#1890ff")}>
                      <Statistic
                        title={
                          <Typography.Text
                            style={{ fontSize: "12px", color: "#8c8c8c" }}>
                            Total Requests
                          </Typography.Text>
                        }
                        value={applicationAction?.request_count || 0}
                        prefix={
                          <FileTextOutlined
                            style={{
                              color: "#1890ff",
                              marginRight: "8px",
                              fontSize: "16px",
                            }}
                          />
                        }
                        valueStyle={{
                          fontSize: "18px",
                          fontWeight: 600,
                          color: "#262626",
                          lineHeight: "1.4",
                        }}
                        formatter={(value) => (
                          <Tooltip title={value}>
                            <span>{value}</span>
                          </Tooltip>
                        )}
                      />
                    </div>
                  </Col>
                  <Col xs={24} sm={8}>
                    <div style={styles.statsCard("#52c41a")}>
                      <Statistic
                        title={
                          <Typography.Text
                            style={{ fontSize: "12px", color: "#8c8c8c" }}>
                            Last Activity
                          </Typography.Text>
                        }
                        value={
                          applicationAction?.last_activity
                            ? moment(applicationAction?.last_activity).fromNow()
                            : "Never"
                        }
                        prefix={
                          <HistoryOutlined
                            style={{
                              color: "#52c41a",
                              marginRight: "8px",
                              fontSize: "16px",
                            }}
                          />
                        }
                        valueStyle={{
                          fontSize: "18px",
                          fontWeight: 600,
                          color: "#262626",
                          lineHeight: "1.4",
                        }}
                        formatter={(value) => (
                          <Tooltip title={value}>
                            <span>{value}</span>
                          </Tooltip>
                        )}
                      />
                    </div>
                  </Col>
                  <Col xs={24} sm={8}>
                    <div style={styles.statsCard("#722ed1")}>
                      <Statistic
                        title={
                          <Typography.Text
                            style={{ fontSize: "12px", color: "#8c8c8c" }}>
                            Date Created
                          </Typography.Text>
                        }
                        value={
                          applicationAction?.date_created
                            ? moment(applicationAction.date_created).format(
                                "MMM D, YYYY"
                              )
                            : "-"
                        }
                        prefix={
                          <CalendarOutlined
                            style={{
                              color: "#722ed1",
                              marginRight: "8px",
                              fontSize: "16px",
                            }}
                          />
                        }
                        valueStyle={{
                          fontSize: "18px",
                          fontWeight: 600,
                          color: "#262626",
                          lineHeight: "1.4",
                        }}
                        formatter={(value) => (
                          <Tooltip title={value}>
                            <span>{value}</span>
                          </Tooltip>
                        )}
                      />
                    </div>
                  </Col>
                </Row>
              </Col>

              {/* Details Section */}
              <Col span={24}>
                <div style={styles.detailsContainer}>
                  <div style={styles.detailsHeader}>
                    <Typography.Title
                      level={5}
                      style={{ margin: 0, fontWeight: 600 }}>
                      Details
                    </Typography.Title>
                  </div>
                  <div style={styles.detailsContent}>
                    <Row gutter={[0, 24]}>
                      <Col span={24}>
                        <Typography.Text style={styles.subtitle}>
                          Description
                          <AiStars style={styles.aiIconStyle} />
                        </Typography.Text>
                        <Typography.Paragraph
                          style={{
                            whiteSpace: "pre-wrap",
                            margin: 0,
                            fontSize: "14px",
                            lineHeight: "1.6",
                          }}>
                          {getDescription()}
                        </Typography.Paragraph>
                      </Col>
                      <Col span={24}>
                        <Typography.Text style={styles.subtitle}>
                          Tags
                          <AiStars style={styles.aiIconStyle} />
                        </Typography.Text>
                        <div>
                          <Space wrap size={8}>
                            {getTags().map((tag: string) => (
                              <Tag
                                key={tag}
                                color="blue"
                                style={styles.tagStyle}>
                                {tag}
                              </Tag>
                            ))}
                            {getTags().length === 0 && (
                              <Typography.Text type="secondary">
                                No tags available
                              </Typography.Text>
                            )}
                          </Space>
                        </div>
                      </Col>
                      <Col span={24}>
                        <Typography.Text style={styles.subtitle}>
                          Notifications
                        </Typography.Text>
                        <Typography.Paragraph
                          style={{ margin: 0, fontSize: "14px" }}>
                          <Tag
                            color={
                              applicationAction?.notifications_enabled
                                ? "success"
                                : "default"
                            }
                            style={styles.tagStyle}>
                            {applicationAction?.notifications_enabled
                              ? "Enabled"
                              : "Disabled"}
                          </Tag>
                        </Typography.Paragraph>
                      </Col>
                      <Col span={24}>
                        <Typography.Text style={styles.subtitle}>
                          Intent Group
                        </Typography.Text>
                        <Typography.Paragraph
                          style={{ margin: 0, fontSize: "14px" }}>
                          {intentGroup ? (
                            <Link
                              to={`/intent-group/${intentGroup.id}`}
                              style={{ color: "#1890ff" }}>
                              {intentGroup.intent_name}
                            </Link>
                          ) : (
                            "-"
                          )}
                        </Typography.Paragraph>
                      </Col>
                    </Row>
                  </div>
                </div>
              </Col>
            </Row>
          </div>
        </TabPane>

        {/* Requests Tab */}
        <TabPane
          tab={
            <span
              style={{
                padding: "0 4px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}>
              <SearchOutlined style={styles.tabIcon} />
              Requests
            </span>
          }
          key="requests">
          <div style={styles.contentCard}>
            <div style={{ marginBottom: "24px" }}>
              <Row gutter={[16, 16]} align="middle">
                <Col>
                  <DatePicker
                    placeholder="Start Date"
                    onChange={(date: Dayjs | null) => setStartDate(date)}
                    value={startDate}
                    disabledDate={(current: Dayjs) =>
                      endDate ? current > endDate : false
                    }
                    style={{ width: "160px" }}
                    format="YYYY-MM-DD"
                    allowClear
                  />
                </Col>
                <Col>
                  <DatePicker
                    placeholder="End Date"
                    onChange={(date: Dayjs | null) => setEndDate(date)}
                    value={endDate}
                    disabledDate={(current: Dayjs) =>
                      startDate ? current < startDate : false
                    }
                    style={{ width: "160px" }}
                    format="YYYY-MM-DD"
                    allowClear
                  />
                </Col>
                <Col>
                  <Button
                    onClick={handleClearFilters}
                    disabled={!endDate && !startDate}>
                    Clear
                  </Button>
                </Col>
                <Col>
                  <Button
                    type="primary"
                    icon={<SearchOutlined />}
                    onClick={() => {
                      requestsByPageRef.current = {};
                      setRequestsCurrentPage(1);
                      fetchRequests(1);
                    }}
                    disabled={!startDate && !endDate}>
                    Search
                  </Button>
                </Col>
              </Row>
            </div>

            <div style={styles.tableContainer}>
              <Table
                rowKey="request_id"
                columns={requestColumns}
                dataSource={requests}
                loading={requestsLoading}
                rowClassName={() => "interactive-row"}
                pagination={{
                  current: requestsCurrentPage,
                  pageSize: requestPageSize,
                  total: requestsTotal,
                  onChange: (page, pageSize) => {
                    setRequestsCurrentPage(page);
                    if (pageSize && pageSize !== requestPageSize) {
                      setRequestPageSize(pageSize);
                      requestsByPageRef.current = {};
                    }
                  },
                  showSizeChanger: true,
                  pageSizeOptions: ["10", "20", "50", "100"],
                  onShowSizeChange: (current, size) => {
                    setRequestsCurrentPage(1);
                    setRequestPageSize(size);
                    requestsByPageRef.current = {};
                  },
                }}
                locale={{
                  emptyText: (
                    <Empty
                      description={
                        <div>
                          <Typography.Text
                            strong
                            style={{ display: "block", marginBottom: "8px" }}>
                            No requests found
                          </Typography.Text>
                          <Typography.Text type="secondary">
                            Try adjusting your date filters or check back later.
                          </Typography.Text>
                        </div>
                      }
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                    />
                  ),
                }}
              />
            </div>
          </div>
        </TabPane>

        {/* Datasets Tab */}
        <TabPane
          tab={
            <span
              style={{
                padding: "0 4px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}>
              <FileTextOutlined style={styles.tabIcon} />
              Datasets
            </span>
          }
          key="datasets">
          <div style={styles.contentCard}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "24px",
              }}>
              <Typography.Title
                level={5}
                style={{ margin: 0, fontWeight: 600 }}>
                Datasets
              </Typography.Title>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() =>
                  navigate(
                    `/models/datasets/new?application_id=${applicationId}&intent_id=${actionId}`
                  )
                }>
                New Dataset
              </Button>
            </div>
            <DatasetsTable
              datasets={datasets}
              loading={datasetsLoading}
              total={datasetsTotal}
              currentPage={datasetsCurrentPage}
              pageSize={datasetsPageSize}
              onPageChange={setDatasetsCurrentPage}
              onRefreshData={fetchDatasets}
              setLoading={setDatasetsLoading}
            />
          </div>
        </TabPane>

        {/* Models Tab */}
        <TabPane
          tab={
            <span
              style={{
                padding: "0 4px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}>
              <ApiOutlined style={styles.tabIcon} />
              Models
            </span>
          }
          key="models">
          <div style={styles.contentCard}>
            <ModelTable
              apiKey={apiKey}
              intentId={Number(actionId)}
              applicationId={Number(applicationId)}
            />
          </div>
        </TabPane>

        {/* Configuration Tab */}
        <TabPane
          tab={
            <span
              style={{
                padding: "0 4px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}>
              <SettingOutlined style={styles.tabIcon} />
              Configuration
            </span>
          }
          key="configuration">
          <div style={styles.contentCard}>
            <ConfigurationPanel
              intentGroupId={intentGroup?.id}
              config={config}
              updateConfig={updateConfig}
              resetConfig={resetConfig}
              showSystemPrompt={false}
            />
          </div>
        </TabPane>
      </Tabs>
    </div>
  );
};

export default ApplicationActionOverview;
